import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, Bed as BedIcon } from 'lucide-react';
import { getAdmission } from '@/lib/ipdApi';
import { usePermission } from '@/hooks/usePermission';

import IPDOverviewTab          from '@/components/IPD/IPDOverviewTab';
import IPDVitalsTab            from '@/components/IPD/IPDVitalsTab';
import IPDMedicationOrdersTab  from '@/components/IPD/IPDMedicationOrdersTab';
import IPDProgressNotesTab     from '@/components/IPD/IPDProgressNotesTab';
import IPDChargesTab           from '@/components/IPD/IPDChargesTab';
import IPDDischargeSummaryTab  from '@/components/IPD/IPDDischargeSummaryTab';
import { PatientAdmissionBar, BillingTotalsBar } from '@/components/IPD/IPDAdmissionBars';
import { TransferModal, DischargeModal, DatesModal } from '@/components/IPD/IPDAdmissionModals';

const TABS = ['overview', 'vitals', 'orders', 'notes', 'charges', 'discharge'] as const;
type Tab = typeof TABS[number];

export default function IPDAdmissionDetailPage() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const { can, hasFeature } = usePermission();
  const canWrite = can('ipd:write');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const backTo = (location.state as { from?: string } | null)?.from || '/ipd';

  const activeTab = (searchParams.get('tab') as Tab) || 'overview';
  const setTab    = (tab: Tab) => setSearchParams({ tab }, { replace: true });

  const [admission, setAdmission] = useState<any>(null);
  const [loading,   setLoading]   = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [billingRefresh, setBillingRefresh] = useState(0);

  const loadAdmission = useCallback(() => {
    if (!admissionId) return;
    return getAdmission(admissionId)
      .then(data => setAdmission(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [admissionId]);

  useEffect(() => { loadAdmission(); }, [loadAdmission]);

  if (!admissionId) return null;

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b shrink-0">
        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => navigate(backTo)} aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Button>

        {loading ? (
          <div className="h-5 w-48 rounded bg-muted animate-pulse" />
        ) : !admission ? (
          <span className="text-sm text-muted-foreground">Admission not found</span>
        ) : null}
      </div>

      {/* Tabs */}
      {loading ? (
        <div className="flex-1 px-4 py-4 text-sm text-muted-foreground">Loading…</div>
      ) : !admission ? (
        <div className="flex flex-col items-center justify-center flex-1 text-center px-4">
          <AlertTriangle className="w-8 h-8 text-muted-foreground mb-2" />
          <h2 className="text-base font-semibold mb-1">Admission Not Found</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            This admission doesn't exist or you don't have access to it.
          </p>
          <Link to={backTo} className="text-sm text-primary mt-3 hover:underline">Back</Link>
        </div>
      ) : (
      <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
        <PatientAdmissionBar
          admission={admission}
          canWrite={canWrite}
          onTransferClick={() => setTransferOpen(true)}
          onDischargeClick={() => setDischargeOpen(true)}
          onDatesClick={() => setDatesOpen(true)}
        />
        <BillingTotalsBar admission={admission} refreshKey={billingRefresh} />
        <Tabs value={activeTab} onValueChange={v => setTab(v as Tab)}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="vitals">Vitals</TabsTrigger>
            <TabsTrigger value="orders">Medication Orders</TabsTrigger>
            <TabsTrigger value="notes">Progress Notes</TabsTrigger>
            <TabsTrigger value="charges">Charges</TabsTrigger>
            <TabsTrigger value="discharge">Discharge Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <IPDOverviewTab admission={admission} />
          </TabsContent>

          <TabsContent value="vitals">
            <IPDVitalsTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} admissionDate={admission?.admission_date} dischargeDate={admission?.discharge_date} />
          </TabsContent>

          <TabsContent value="orders">
            <IPDMedicationOrdersTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} />
          </TabsContent>

          <TabsContent value="notes">
            <IPDProgressNotesTab admissionId={admissionId} isActive={canWrite && admission.status === 'admitted'} admissionDate={admission?.admission_date} dischargeDate={admission?.discharge_date} />
          </TabsContent>

          <TabsContent value="charges">
            <IPDChargesTab admissionId={admissionId} admission={admission} canWrite={canWrite} onChargesChanged={() => { setBillingRefresh(v => v + 1); loadAdmission(); }} />
          </TabsContent>

          <TabsContent value="discharge">
            <IPDDischargeSummaryTab admissionId={admissionId} admissionInfo={admission} onFinalized={loadAdmission} />
          </TabsContent>
        </Tabs>
      </div>
      )}

      {admission && (
        <TransferModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          admission={admission}
          onTransferred={loadAdmission}
        />
      )}

      {admission && (
        <DischargeModal
          open={dischargeOpen}
          onClose={() => setDischargeOpen(false)}
          admission={admission}
          onUpdated={loadAdmission}
        />
      )}

      {admission && (
        <DatesModal
          open={datesOpen}
          onClose={() => setDatesOpen(false)}
          admission={admission}
          onUpdated={loadAdmission}
        />
      )}
    </div>
  );
}
